import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, UserPlus, Copy, Edit2, X, Upload, ImagePlus, Mail, Instagram, Linkedin, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";

const EMPTY = {
  id: null,
  name: "",
  role: "",
  bio: "",
  image: "",
  instagram: "",
  linkedin: "",
  email: "",
};

function slugifyClient(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function TeamAdmin() {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [openMember, setOpenMember] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const [m, setM] = useState(EMPTY);
  const [inv, setInv] = useState({ email: "", role: "team" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/team"),
      api.get("/admin/team/users"),
      api.get("/admin/team/invites"),
    ]);
    setMembers(a.data); setUsers(b.data); setInvites(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setM(EMPTY); setOpenMember(true); };
  const openEdit = (mem) => {
    setM({
      id: mem.id,
      name: mem.name || "",
      role: mem.role || "",
      bio: mem.bio || "",
      image: mem.image || "",
      instagram: mem.instagram || "",
      linkedin: mem.linkedin || "",
      email: mem.email || "",
    });
    setOpenMember(true);
  };

  const slug = m.name ? slugifyClient(m.name) : "new-member";

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    if (!m.name) { toast.error("Set the name first — that determines where the file is stored."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post(`/admin/team/upload?slug=${encodeURIComponent(slug)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data.urls?.[0];
      if (url) setM((prev) => ({ ...prev, image: url }));
      toast.success("Headshot uploaded");
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const saveMember = async (e) => {
    e.preventDefault();
    if (!m.name.trim() || !m.role.trim()) { toast.error("Name and role are required"); return; }
    setSaving(true);
    try {
      const payload = { ...m };
      delete payload.id;
      if (m.id) {
        await api.put(`/admin/team/${m.id}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/admin/team", payload);
        toast.success("Member added");
      }
      setOpenMember(false);
      setM(EMPTY);
      load();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const delMember = async (mem) => {
    if (!window.confirm(`Delete "${mem.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/team/${mem.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  /* ---- Drag-to-reorder (public bios only) ---- */
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = (e, id) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const onDragEnd = () => { dragId.current = null; setOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId.current;
    setOverId(null);
    dragId.current = null;
    if (!fromId || fromId === targetId) return;

    const next = [...members];
    const fromIdx = next.findIndex((r) => r.id === fromId);
    const toIdx = next.findIndex((r) => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setMembers(next); // optimistic

    try {
      await api.post("/admin/team/reorder", { ids: next.map((r) => r.id) });
    } catch (err) {
      toast.error("Reorder failed — reverting");
      load();
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/team/invite", inv);
      navigator.clipboard.writeText(data.invite_url);
      toast.success("Invite sent. Link copied.");
      setOpenInvite(false); setInv({ email: "", role: "team" }); load();
    } catch { toast.error("Invite failed"); }
  };

  const copyInvite = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/register?invite=${token}`);
    toast.success("Invite URL copied");
  };

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-8">
        <div><div className="label-mono mb-2">/ People</div><h1 className="font-display text-4xl font-bold tracking-tighter">Team</h1></div>
        <div className="flex gap-2">
          <button onClick={() => setOpenInvite(true)} className="glass-btn" data-testid="invite-new"><UserPlus size={14}/> Invite</button>
          <button onClick={openNew} className="glass-btn solid" data-testid="member-new"><Plus size={14}/> Public Bio</button>
        </div>
      </div>

      {/* Public bios */}
      <div className="mb-12">
        <div className="label-mono mb-3 text-cyan-400">/ Public team bios (About page) — {members.length} · drag to reorder</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((mem) => (
            <div
              key={mem.id}
              draggable
              onDragStart={(e) => onDragStart(e, mem.id)}
              onDragOver={(e) => onDragOver(e, mem.id)}
              onDrop={(e) => onDrop(e, mem.id)}
              onDragEnd={onDragEnd}
              className={`relative rounded-2xl border bg-[#121215] p-5 lift group transition-all ${overId === mem.id ? "border-cyan-400/60 ring-2 ring-cyan-400/30" : "border-white/5"}`}
              data-testid={`tm-${mem.id}`}
            >
              <div
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
                title="Drag to reorder"
                aria-label="Drag handle"
                data-testid={`tm-drag-${mem.id}`}
              >
                <GripVertical size={12} />
              </div>
              <div className="flex items-center gap-3 mb-3">
                {mem.image ? (
                  <img src={mem.image} alt={mem.name} className="w-14 h-14 rounded-full object-cover border border-white/10 pointer-events-none"/>
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#0a0a0c] border border-white/10 flex items-center justify-center text-zinc-700"><ImagePlus size={20}/></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{mem.name}</div>
                  <div className="label-mono truncate">{mem.role}</div>
                </div>
              </div>
              <p className="text-sm text-zinc-400 line-clamp-3 min-h-[3.6em]">{mem.bio || <span className="italic text-zinc-600">No bio.</span>}</p>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex gap-2 text-zinc-500">
                  {mem.instagram && <a href={mem.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300" aria-label="Instagram"><Instagram size={14}/></a>}
                  {mem.linkedin && <a href={mem.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300" aria-label="LinkedIn"><Linkedin size={14}/></a>}
                  {mem.email && <a href={`mailto:${mem.email}`} className="hover:text-cyan-300" aria-label="Email"><Mail size={14}/></a>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(mem)} className="glass-btn ghost p-2" aria-label="Edit" data-testid={`tm-edit-${mem.id}`}><Edit2 size={14}/></button>
                  <button onClick={() => delMember(mem)} className="glass-btn ghost p-2" aria-label="Delete" data-testid={`tm-del-${mem.id}`}><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          ))}
          {members.length === 0 && <div className="col-span-full text-zinc-500 text-sm italic">No team bios yet — click "Public Bio" to add one.</div>}
        </div>
      </div>

      {/* Authenticated users */}
      <div className="mb-12">
        <div className="label-mono mb-3 text-cyan-400">/ Authenticated users ({users.length})</div>
        <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0">
              <div><div>{u.name}</div><div className="text-zinc-500 text-xs">{u.email}</div></div>
              <span className="badge muted">{u.role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Invites */}
      <div>
        <div className="label-mono mb-3 text-cyan-400">/ Pending invites</div>
        <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
          {invites.map((i) => (
            <div key={i.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0">
              <div><div>{i.email}</div><div className="text-zinc-500 text-xs">{i.role} · {i.used ? "used" : "pending"}</div></div>
              <button onClick={() => copyInvite(i.token)} className="glass-btn ghost p-2"><Copy size={14}/></button>
            </div>
          ))}
          {invites.length === 0 && <div className="px-5 py-6 text-zinc-500 text-sm">No invites yet.</div>}
        </div>
      </div>

      {/* Member modal */}
      <Modal
        open={openMember}
        onClose={() => setOpenMember(false)}
        wide
        title={m.id ? `Edit · ${m.name}` : "Add Team Bio"}
        testid="member-modal"
      >
        <form onSubmit={saveMember} className="space-y-6" data-testid="member-form">
          <Section label="Identity">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Name *" required value={m.name} onChange={(v) => setM({ ...m, name: v })} testid="tm-name"/>
              <Input label="Role *" required value={m.role} onChange={(v) => setM({ ...m, role: v })} placeholder="Founder / CEO" testid="tm-role"/>
            </div>
            <div>
              <label className="label-mono block mb-2">Bio</label>
              <textarea
                className="pulse-input pulse-textarea"
                rows={4}
                value={m.bio}
                onChange={(e) => setM({ ...m, bio: e.target.value })}
                data-testid="tm-bio"
              />
            </div>
          </Section>

          <Section label="Headshot">
            <div className="flex gap-4 items-start">
              {m.image ? (
                <div className="relative shrink-0">
                  <img src={m.image} alt="" className="w-32 h-32 rounded-full object-cover border border-white/10"/>
                  <button type="button" onClick={() => setM({ ...m, image: "" })} className="absolute -top-1 -right-1 bg-black/80 hover:bg-black p-1.5 rounded-full border border-white/10" aria-label="Remove">
                    <X size={12}/>
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full border border-dashed border-white/15 bg-[#0a0a0c] flex items-center justify-center text-zinc-600">
                  <ImagePlus size={24}/>
                </div>
              )}
              <div className="flex-1 space-y-3">
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(e.target.files)}
                  data-testid="tm-image-file"
                />
                <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading} className="glass-btn ghost" data-testid="tm-image-upload">
                  <Upload size={14}/> {uploading ? "Uploading..." : "Upload headshot"}
                </button>
                <Input
                  label="…or paste an image URL"
                  value={m.image}
                  onChange={(v) => setM({ ...m, image: v })}
                  placeholder="/api/uploads/... or https://..."
                  testid="tm-image-url"
                />
              </div>
            </div>
          </Section>

          <Section label="Contact &amp; Socials">
            <div className="grid sm:grid-cols-3 gap-3">
              <Input label="Instagram" value={m.instagram} onChange={(v) => setM({ ...m, instagram: v })} placeholder="https://instagram.com/..." testid="tm-instagram"/>
              <Input label="LinkedIn" value={m.linkedin} onChange={(v) => setM({ ...m, linkedin: v })} placeholder="https://linkedin.com/in/..." testid="tm-linkedin"/>
              <Input label="Email" type="email" value={m.email} onChange={(v) => setM({ ...m, email: v })} placeholder="name@pulsemedia.vip" testid="tm-email"/>
            </div>
          </Section>

          <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-[#0a0a0c]/95 backdrop-blur border-t border-white/5">
            <button type="submit" disabled={saving || uploading} className="glass-btn solid w-full justify-center" data-testid="tm-save">
              {saving ? "Saving..." : (m.id ? "Update Member" : "Add Member")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Invite modal */}
      <Modal open={openInvite} onClose={() => setOpenInvite(false)} title="Invite Team Member" testid="invite-modal">
        <form onSubmit={sendInvite} className="space-y-3" data-testid="invite-form">
          <Input label="Email *" type="email" required value={inv.email} onChange={(v) => setInv({ ...inv, email: v })} testid="inv-email"/>
          <div>
            <label className="label-mono block mb-2">Role</label>
            <select className="pulse-input" value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })}>
              <option value="team">Team</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button className="glass-btn solid w-full justify-center" data-testid="inv-send">Send Invite</button>
        </form>
      </Modal>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="space-y-3">
      <div className="label-mono text-zinc-500 border-b border-white/5 pb-2">/ {label}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input
        className="pulse-input"
        type={type}
        required={required}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
